/**
 * ShapePacker - 2D bin packing for cut-pattern layout
 *
 * Port of the part-nesting idea in spShapePack.java / spPage.packSmart
 * (Java brute-forces grid positions + 90-degree rotations with polygon
 * collision tests). This is a cleaner equivalent working on part bounding
 * boxes: a bottom-left heuristic with a fixed sheet width and unbounded
 * height, trying 0/90 degree rotations and keeping whichever placement
 * sits highest up the sheet. The goal is dense material usage.
 *
 * packParts(parts, options) has the same return contract as
 * SVGExporter.layoutParts: {placed: [{part, dx, dy}], widthMM, heightMM},
 * where each placed.part carries already-transformed (rotated) polygons
 * so the exporters only ever apply the dx/dy translation. Placed entries
 * additionally carry {rotated, rotCentre}: whether the packer chose the
 * 90-degree orientation and the polygon-space centre it rotated around -
 * consumers that map the polygons back into a SOURCE frame (the flat<->
 * assembled preview animation) must compensate for that rotation.
 */

/**
 * Axis-aligned bounding box of a part's polygon outers (holes are inside).
 * Returns null for degenerate parts with no points.
 */
function partBBox(part) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const poly of part.polygons) {
        for (const p of poly.outer) {
            if (p.x < minX) minX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.x > maxX) maxX = p.x;
            if (p.y > maxY) maxY = p.y;
        }
    }
    if (minX === Infinity) return null;
    return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

/**
 * Return a copy of the part with all polygon points rotated 90 degrees
 * around the bbox centre. (x,y) -> (cx + (y - cy), cy - (x - cx)).
 * The bbox width/height swap; contents are genuinely transformed so the
 * exporters need no rotation awareness.
 */
export function rotatePart90(part, bbox) {
    const cx = (bbox.minX + bbox.maxX) / 2;
    const cy = (bbox.minY + bbox.maxY) / 2;
    const rot = (p) => ({ x: cx + (p.y - cy), y: cy - (p.x - cx) });
    return {
        ...part,
        polygons: part.polygons.map(poly => ({
            outer: poly.outer.map(rot),
            holes: (poly.holes || []).map(ring => ring.map(rot))
        }))
    };
}

/**
 * Lowest y >= margin at which a w x h rect at column x fits above/between
 * the already-placed rects, keeping `gap` clearance. Rects store their
 * placed bbox {x, y, w, h}; overlap is tested on gap-expanded boxes.
 */
function dropY(x, w, h, rects, margin, gap) {
    let y = margin;
    let moved = true;
    while (moved) {
        moved = false;
        for (const r of rects) {
            // Overlap test with gap clearance between boxes
            if (x < r.x + r.w + gap && r.x < x + w + gap &&
                y < r.y + r.h + gap && r.y < y + h + gap) {
                y = r.y + r.h + gap;
                moved = true;
            }
        }
    }
    return y;
}

/**
 * Best bottom-left position for a w x h bbox. Candidate columns are the
 * sheet margin plus the left/right edges of every placed rect; each is
 * dropped to its lowest feasible y and the lowest-y (then lowest-x)
 * result wins. Returns null when the width cannot fit on the sheet.
 */
function findPosition(w, h, rects, sheetWidth, margin, gap) {
    const maxX = sheetWidth - margin - w;
    if (maxX < margin) return null;

    const candidates = new Set([margin]);
    for (const r of rects) {
        candidates.add(r.x);
        candidates.add(r.x + r.w + gap);
    }

    let best = null;
    for (const x of candidates) {
        if (x < margin || x > maxX) continue;
        const y = dropY(x, w, h, rects, margin, gap);
        if (!best || y < best.y || (y === best.y && x < best.x)) {
            best = { x, y };
        }
    }
    return best;
}

/**
 * Pack parts onto a fixed-width sheet growing downward as needed.
 *
 * @param {Array<{name, polygons}>} parts - From SVGExporter.collectParts
 * @param {Object} [options]
 * @param {number} [options.sheetWidth=800] - Sheet width in mm
 * @param {number} [options.gap=10] - Minimum clearance between parts in mm
 * @param {number} [options.margin=10] - Sheet border margin in mm
 * @param {boolean} [options.allowRotation=true] - Try 90-degree rotation
 * @returns {{placed: Array<{part, dx: number, dy: number}>, widthMM: number, heightMM: number}}
 */
export function packParts(parts, options = {}) {
    const {
        sheetWidth = 800,
        gap = 10,
        margin = 10,
        allowRotation = true
    } = options;

    // Sort by bbox area, largest first (stable: keeps input order on ties)
    const order = parts
        .map(part => ({ part, bbox: partBBox(part) }))
        .filter(e => e.bbox)
        .sort((a, b) => (b.bbox.w * b.bbox.h) - (a.bbox.w * a.bbox.h));

    const rects = [];   // placed bboxes {x, y, w, h}
    const placed = [];
    let sheetRight = 0;
    let sheetBottom = 0;

    for (const { part, bbox } of order) {
        // Orientation 0: as drawn
        const tries = [{ part, bbox, rotated: false, pos: findPosition(bbox.w, bbox.h, rects, sheetWidth, margin, gap) }];

        // Orientation 90: genuinely rotate the polygons around bbox centre
        if (allowRotation && bbox.w !== bbox.h) {
            const rotated = rotatePart90(part, bbox);
            const rBBox = partBBox(rotated);
            tries.push({ part: rotated, bbox: rBBox, rotated: true, pos: findPosition(rBBox.w, rBBox.h, rects, sheetWidth, margin, gap) });
        }

        // Keep whichever orientation places higher up; on equal y prefer
        // the lower resulting bottom edge (grows the sheet less), then
        // the further-left position
        let best = null;
        for (const t of tries) {
            if (!t.pos) continue;
            if (!best ||
                t.pos.y < best.pos.y ||
                (t.pos.y === best.pos.y && t.pos.y + t.bbox.h < best.pos.y + best.bbox.h) ||
                (t.pos.y === best.pos.y && t.bbox.h === best.bbox.h && t.pos.x < best.pos.x)) {
                best = t;
            }
        }

        if (!best) {
            // Wider than the sheet in every orientation: pin to the left
            // margin on its own row (same as the row layout's overflow)
            const t = tries.reduce((a, b) => (b.bbox.w < a.bbox.w ? b : a));
            best = { ...t, pos: { x: margin, y: dropY(margin, t.bbox.w, t.bbox.h, rects, margin, gap) } };
        }

        const { x, y } = best.pos;
        const b = best.bbox;
        rects.push({ x, y, w: b.w, h: b.h });
        placed.push({
            part: best.part,
            dx: x - b.minX,
            dy: y - b.minY,
            // rotatePart90 rotates around the bbox centre, which is
            // invariant under that rotation - so the placed orientation's
            // bbox centre IS the rotation centre in polygon space
            rotated: best.rotated,
            rotCentre: { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 }
        });
        if (x + b.w > sheetRight) sheetRight = x + b.w;
        if (y + b.h > sheetBottom) sheetBottom = y + b.h;
    }

    return {
        placed,
        widthMM: Math.ceil(sheetRight + margin),
        heightMM: Math.ceil(sheetBottom + margin)
    };
}

/**
 * Pack parts into the most SQUARE footprint we can find, rather than onto a
 * sheet of a given width.
 *
 * A cut sheet has a width the material dictates, so packParts takes one and
 * grows downward. A 3D printer bed does not: there the parts just want to sit
 * in a compact blob, and a chair's parts (a few long panels plus a crowd of
 * small ribs) pinned to a material width come out as a long strip that wastes
 * the bed and reads badly in the preview.
 *
 * There is no closed form for the width that squares a bottom-left packing, so
 * this sweeps candidate widths around the ideal-efficiency square side and
 * keeps whichever run comes out closest to square. packParts is cheap and a
 * chair has tens of parts, so the sweep costs nothing noticeable.
 *
 * @param {Array<{name, polygons}>} parts - From SVGExporter.collectParts
 * @param {Object} [options]
 * @param {number} [options.gap=10] - Minimum clearance between parts in mm
 * @param {number} [options.margin=10] - Border margin in mm
 * @param {boolean} [options.allowRotation=true] - Try 90-degree rotation
 * @returns {{placed: Array<{part, dx, dy, rotated, rotCentre}>, widthMM: number,
 *   heightMM: number, sheetWidth: number}} Same contract as packParts, plus the
 *   width that won
 */
export function packPartsSquare(parts, options = {}) {
    const { gap = 10, margin = 10, allowRotation = true } = options;

    const boxes = parts.map(partBBox).filter(Boolean);
    if (boxes.length === 0) {
        return { ...packParts(parts, options), sheetWidth: 2 * margin };
    }

    // Side of the square that would hold every part at perfect efficiency
    // (each part claims its bbox plus its share of the gap)
    const area = boxes.reduce((sum, b) => sum + (b.w + gap) * (b.h + gap), 0);
    const ideal = Math.sqrt(area) + 2 * margin;

    // Nothing can be packed narrower than the widest part - in its SLIMMER
    // orientation when the packer is allowed to turn it
    const widest = Math.max(...boxes.map(
        b => (allowRotation ? Math.min(b.w, b.h) : b.w)));
    const minWidth = widest + 2 * margin;

    let best = null;
    for (let k = 0; k <= 14; k++) {
        const sheetWidth = Math.max(minWidth, ideal * (0.6 + k * 0.1)); // 0.6x..2.0x
        const run = packParts(parts, { sheetWidth, gap, margin, allowRotation });
        const long = Math.max(run.widthMM, run.heightMM);
        const short = Math.min(run.widthMM, run.heightMM);
        const aspect = short > 0 ? long / short : Infinity; // 1 = square
        if (!best || aspect < best.aspect) best = { run, aspect, sheetWidth };
    }

    return { ...best.run, sheetWidth: best.sheetWidth };
}

/**
 * Pack parts onto fixed-size sheets (width x height), starting a new page
 * whenever a part cannot fit within the sheet height. Same bottom-left
 * heuristic as packParts within each page. Parts too large for the sheet
 * in any orientation get a page of their own, overflowing its bounds
 * (same spirit as packParts' width-overflow behaviour).
 *
 * @param {Array<{name, polygons}>} parts - From SVGExporter.collectParts
 * @param {Object} [options]
 * @param {number} [options.sheetWidth=800] - Sheet width in mm
 * @param {number} [options.sheetHeight=600] - Sheet height in mm
 * @param {number} [options.gap=10] - Minimum clearance between parts in mm
 * @param {number} [options.margin=10] - Sheet border margin in mm
 * @param {boolean} [options.allowRotation=true] - Try 90-degree rotation
 * @returns {{pages: Array<{placed: Array<{part, dx, dy}>, widthMM: number, heightMM: number}>, pageCount: number}}
 */
export function packPartsPaged(parts, options = {}) {
    const {
        sheetWidth = 800,
        sheetHeight = 600,
        gap = 10,
        margin = 10,
        allowRotation = true
    } = options;

    const maxBottom = sheetHeight - margin;

    // Sort by bbox area, largest first (stable: keeps input order on ties)
    let remaining = parts
        .map(part => ({ part, bbox: partBBox(part) }))
        .filter(e => e.bbox)
        .sort((a, b) => (b.bbox.w * b.bbox.h) - (a.bbox.w * a.bbox.h));

    const pages = [];

    while (remaining.length > 0) {
        const rects = [];
        const placed = [];
        const leftover = [];
        let sheetRight = 0;
        let sheetBottom = 0;
        let ownPage = false;

        for (let i = 0; i < remaining.length; i++) {
            const { part, bbox } = remaining[i];

            // Orientation 0: as drawn
            const tries = [{ part, bbox, rotated: false, pos: findPosition(bbox.w, bbox.h, rects, sheetWidth, margin, gap) }];

            // Orientation 90: genuinely rotate the polygons around bbox centre
            if (allowRotation && bbox.w !== bbox.h) {
                const rotated = rotatePart90(part, bbox);
                const rBBox = partBBox(rotated);
                tries.push({ part: rotated, bbox: rBBox, rotated: true, pos: findPosition(rBBox.w, rBBox.h, rects, sheetWidth, margin, gap) });
            }

            // Same preference as packParts, but placements running past the
            // sheet bottom are rejected
            let best = null;
            for (const t of tries) {
                if (!t.pos) continue;
                if (t.pos.y + t.bbox.h > maxBottom) continue;
                if (!best ||
                    t.pos.y < best.pos.y ||
                    (t.pos.y === best.pos.y && t.pos.y + t.bbox.h < best.pos.y + best.bbox.h) ||
                    (t.pos.y === best.pos.y && t.bbox.h === best.bbox.h && t.pos.x < best.pos.x)) {
                    best = t;
                }
            }

            if (!best) {
                if (rects.length === 0) {
                    // Cannot fit even on an empty sheet: oversized part gets
                    // a page of its own, overflowing the sheet bounds. Prefer
                    // an orientation whose width fits, else the narrowest.
                    const t = tries.find(tr => tr.pos) ||
                        tries.reduce((a, b) => (b.bbox.w < a.bbox.w ? b : a));
                    const pos = t.pos || { x: margin, y: margin };
                    placed.push({
                        part: t.part, dx: pos.x - t.bbox.minX, dy: pos.y - t.bbox.minY,
                        rotated: t.rotated,
                        rotCentre: { x: (t.bbox.minX + t.bbox.maxX) / 2, y: (t.bbox.minY + t.bbox.maxY) / 2 }
                    });
                    sheetRight = pos.x + t.bbox.w;
                    sheetBottom = pos.y + t.bbox.h;
                    // Everything else moves on to later pages
                    leftover.push(...remaining.slice(i + 1));
                    ownPage = true;
                    break;
                }
                // No room left on this page: try again on the next one
                leftover.push(remaining[i]);
                continue;
            }

            const { x, y } = best.pos;
            const b = best.bbox;
            rects.push({ x, y, w: b.w, h: b.h });
            placed.push({
                part: best.part, dx: x - b.minX, dy: y - b.minY,
                rotated: best.rotated,
                rotCentre: { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 }
            });
            if (x + b.w > sheetRight) sheetRight = x + b.w;
            if (y + b.h > sheetBottom) sheetBottom = y + b.h;
        }

        pages.push({
            placed,
            widthMM: Math.ceil(sheetRight + margin),
            heightMM: Math.ceil(sheetBottom + margin),
            oversized: ownPage
        });
        remaining = leftover;
    }

    return { pages, pageCount: pages.length };
}
